import { RapidStateCreator } from "../types";
import type {
    ModelChange,
    ModelPageState,
    ModelMetrics,
    ClassMetrics,
    TestSource,
    TestSourceWithInference
} from "@/components/flow/deploy/Steps/Model/types";
import {
    ModelPageState as PageState,
    ModelWidgetState,
    ModelArchitecture,
    ModelChangeTrigger
} from "@/components/flow/deploy/Steps/Model/types";
import { markModelChangeAsSeen, getVersionMetrics, getTestSources } from "@/utils/api/modelChanges";
import { streamInfer } from "@/utils/api/inference";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { CIEvaluationResult } from "@functions/continuousImprovement/continuousImprovementTypes";
import {
    AvailableRapidModelArchitectures,
    RapidTrainStatus
} from "@/components/flow/shared/Deploy/documentationCms";
import { MODEL_COMPARISON_IMAGE_LIMIT } from "@/components/flow/deploy/Steps/Model/constants";

export interface ModelSliceState {
    // Page state
    modelPageState: ModelPageState;

    // Current model change being displayed
    currentModelChange: ModelChange | null;

    // All model changes (for history dropdown)
    modelChanges: ModelChange[];

    // Legacy model flag (when there's a trained model but no modelChanges history)
    hasLegacyModel: boolean;

    // Loading and error state
    modelChangeLoading: boolean;
    modelChangeError: string | null;

    // Model metrics (mAP, precision, recall, etc.)
    currentModelMetrics: ModelMetrics | null;
    previousModelMetrics: ModelMetrics | null;
    currentClassMetrics: ClassMetrics[] | null;
    previousClassMetrics: ClassMetrics[] | null;
    metricsLoading: boolean;
    metricsError: string | null;

    // Model comparison (test sources with inference results)
    comparisonSources: TestSourceWithInference[];
    comparisonLoading: boolean;
    comparisonError: string | null;

    // Getters
    getModelPageState: () => ModelPageState;
    getCurrentModelChange: () => ModelChange | null;
    getModelChanges: () => ModelChange[];
    getLatestUnseenModelChange: (userId: string) => ModelChange | null;
    hasUnseenModelChange: (userId: string) => boolean;
    getHasLegacyModel: () => boolean;
    getCurrentModelMetrics: () => ModelMetrics | null;
    getPreviousModelMetrics: () => ModelMetrics | null;
    getCurrentClassMetrics: () => ClassMetrics[] | null;
    getPreviousClassMetrics: () => ClassMetrics[] | null;
    getComparisonSources: () => TestSourceWithInference[];
    getIsTraining: () => boolean;
    getModelWidgetState: (userId: string) => ModelWidgetState;
    getCurrentVersionLabel: () => string | null;

    // Setters
    setModelPageState: (state: ModelPageState) => void;
    setCurrentModelChange: (change: ModelChange | null) => void;
    setModelChanges: (changes: ModelChange[]) => void;
    setModelChangeLoading: (loading: boolean) => void;
    setModelChangeError: (error: string | null) => void;

    // Actions
    initializeFromRapidData: (rapidData: any, userId: string) => void;
    markCurrentChangeAsSeen: (rapidDataId: string, userId: string) => Promise<void>;
    fetchCurrentModelMetrics: () => Promise<void>;
    fetchComparisonMetrics: () => Promise<void>;
    fetchComparisonSources: (rapidDataId: string, apiKey: string) => Promise<void>;
    addUploadedSource: (source: TestSourceWithInference) => void;
    runInferenceForSource: (
        source: { id: string; fileId: string },
        apiKey: string
    ) => Promise<void>;
    clearModelState: () => void;
}

/**
 * Extract version number from a model ref string (e.g., "dataset-url/3" -> "v3")
 */
export function extractVersionFromRef(ref: string | undefined): string | null {
    if (!ref) return null;
    const match = ref.match(/\/(\d+)$/);
    return match ? `v${match[1]}` : null;
}

/**
 * Check if rapidData has a trained model (either rf_detr or rf_instant with FINISHED status)
 */
export function hasTrainedModel(rapidData: any): boolean {
    if (!rapidData?.models) return false;

    const rfDetrModels = rapidData.models[AvailableRapidModelArchitectures.RF_DETR] ?? [];
    const rfInstantModels = rapidData.models[AvailableRapidModelArchitectures.RF_INSTANT] ?? [];

    const hasFinishedRfDetr = rfDetrModels.some(
        (m: any) => m?.train?.status === RapidTrainStatus.FINISHED
    );
    const hasFinishedRfInstant = rfInstantModels.some(
        (m: any) => m?.train?.status === RapidTrainStatus.FINISHED
    );

    return hasFinishedRfDetr || hasFinishedRfInstant;
}

/**
 * Create a synthetic ModelChange from legacy model data for backwards compatibility
 */
function createLegacyModelChange(rapidData: any): ModelChange {
    const rfDetrModels = rapidData.models[AvailableRapidModelArchitectures.RF_DETR] ?? [];
    const rfInstantModels = rapidData.models[AvailableRapidModelArchitectures.RF_INSTANT] ?? [];

    // Find the latest finished model
    const finishedRfDetr = rfDetrModels.filter(
        (m: any) => m?.train?.status === RapidTrainStatus.FINISHED
    );
    const finishedRfInstant = rfInstantModels.filter(
        (m: any) => m?.train?.status === RapidTrainStatus.FINISHED
    );

    // Determine which architecture is the "current" model
    // Prefer RF_DETR if it's trained, otherwise RF_INSTANT
    const hasRfDetr = finishedRfDetr.length > 0;
    const latestModel = hasRfDetr
        ? finishedRfDetr[finishedRfDetr.length - 1]
        : finishedRfInstant[finishedRfInstant.length - 1];

    const architecture = hasRfDetr ? ModelArchitecture.RF_DETR : ModelArchitecture.RF_INSTANT;

    // Try to extract timestamp from the model's train data
    // finishedAt could be: Firebase Timestamp {seconds, nanoseconds}, {_seconds, _nanoseconds}, or a date string
    const finishedAt = latestModel?.train?.finishedAt;
    let timestampSeconds = Math.floor(Date.now() / 1000); // default to now

    if (finishedAt) {
        if (typeof finishedAt === "object") {
            // Firebase Timestamp object - check both formats
            timestampSeconds =
                finishedAt._seconds ?? finishedAt.seconds ?? Math.floor(Date.now() / 1000);
        } else if (typeof finishedAt === "string" || typeof finishedAt === "number") {
            // Date string or Unix timestamp
            const parsed = new Date(finishedAt).getTime();
            if (!isNaN(parsed)) {
                timestampSeconds = Math.floor(parsed / 1000);
            }
        }
    }

    const timestamp = { _seconds: timestampSeconds, _nanoseconds: 0 };

    return {
        id: "legacy",
        timestamp,
        from: null,
        to: { architecture, ref: latestModel?.id },
        trigger: ModelChangeTrigger.INITIAL_TRAINING,
        imageCountAtTraining: 0, // Unknown for legacy models
        classes: [], // Unknown for legacy models
        seenBy: [] // Legacy models are considered "seen" - no comparison needed
    };
}

// Tracks the current fetchComparisonSources invocation to prevent stale updates from
// duplicate calls (e.g., when useEffect fires multiple times). Each new call increments
// the counter; only the latest call is allowed to write state updates.
let _activeFetchComparisonId = 0;

export const createModelSlice: RapidStateCreator<ModelSliceState> = (set, get) => ({
    // Initial state
    modelPageState: PageState.LOADING,
    currentModelChange: null,
    modelChanges: [],
    hasLegacyModel: false,
    modelChangeLoading: false,
    modelChangeError: null,
    currentModelMetrics: null,
    previousModelMetrics: null,
    currentClassMetrics: null,
    previousClassMetrics: null,
    metricsLoading: false,
    metricsError: null,
    comparisonSources: [],
    comparisonLoading: false,
    comparisonError: null,

    // Getters
    getModelPageState: () => get().modelPageState,
    getCurrentModelChange: () => get().currentModelChange,
    getModelChanges: () => get().modelChanges,
    getHasLegacyModel: () => get().hasLegacyModel,
    getCurrentModelMetrics: () => get().currentModelMetrics,
    getPreviousModelMetrics: () => get().previousModelMetrics,
    getCurrentClassMetrics: () => get().currentClassMetrics,
    getPreviousClassMetrics: () => get().previousClassMetrics,
    getComparisonSources: () => get().comparisonSources,

    getIsTraining: () => {
        const rapidData = (get() as any).currentRapidData;
        return rapidData?.lastCIEvaluation?.result === CIEvaluationResult.TRAINING_IN_PROGRESS;
    },

    getModelWidgetState: (userId: string) => {
        if (!userId) return ModelWidgetState.DEFAULT;
        if (get().getIsTraining()) return ModelWidgetState.TRAINING;

        // Read directly from currentRapidData (live Firestore data) rather than
        // modelSlice.modelChanges which is only refreshed on init
        const rapidData = (get() as any).currentRapidData;
        const changes = rapidData?.modelChanges ?? [];
        if (changes.length === 0) return ModelWidgetState.DEFAULT;

        const latestChange = changes[changes.length - 1];
        const hasFromRef = !!latestChange.from?.ref;
        const isUnseen = !latestChange.seenBy.includes(userId);

        if (isUnseen && hasFromRef) return ModelWidgetState.UNSEEN_CHANGE;

        return ModelWidgetState.DEFAULT;
    },

    getCurrentVersionLabel: () => {
        const currentChange = get().currentModelChange;
        return extractVersionFromRef(currentChange?.to?.ref);
    },

    getLatestUnseenModelChange: (userId: string) => {
        // Read from live Firestore data for consistency with getModelWidgetState
        if (!userId) return null;
        const rapidData = (get() as any).currentRapidData;
        const changes = rapidData?.modelChanges ?? [];
        if (changes.length === 0) return null;

        const latestChange = changes[changes.length - 1];
        if (latestChange.seenBy.includes(userId)) return null;
        if (!latestChange.from?.ref) return null;

        return latestChange;
    },

    hasUnseenModelChange: (userId: string) => {
        return get().getLatestUnseenModelChange(userId) !== null;
    },

    // Setters
    setModelPageState: (state) => set({ modelPageState: state }, false, "setModelPageState"),
    setCurrentModelChange: (change) =>
        set({ currentModelChange: change }, false, "setCurrentModelChange"),
    setModelChanges: (changes) => set({ modelChanges: changes }, false, "setModelChanges"),
    setModelChangeLoading: (loading) =>
        set({ modelChangeLoading: loading }, false, "setModelChangeLoading"),
    setModelChangeError: (error) => set({ modelChangeError: error }, false, "setModelChangeError"),

    // Actions
    initializeFromRapidData: (rapidData: any, userId: string) => {
        const changes = rapidData?.modelChanges ?? [];

        // Clear stale metrics and comparison data from a previously viewed model.
        // This prevents showing data from model A when switching to model B.
        set(
            {
                modelChanges: changes,
                currentModelMetrics: null,
                previousModelMetrics: null,
                currentClassMetrics: null,
                previousClassMetrics: null,
                metricsLoading: false,
                metricsError: null,
                comparisonSources: [],
                comparisonLoading: false,
                comparisonError: null
            },
            false,
            "initializeFromRapidData:clearAndSetChanges"
        );

        if (changes.length === 0) {
            // No model changes history - check if there's a legacy trained model
            const hasTrained = hasTrainedModel(rapidData);
            if (hasTrained) {
                // Legacy model exists - create a synthetic model change for display
                // Use SINGLE_MODEL_PERFORMANCE to show test images and metrics
                const legacyChange = createLegacyModelChange(rapidData);
                set(
                    {
                        currentModelChange: legacyChange,
                        hasLegacyModel: true,
                        modelPageState: PageState.SINGLE_MODEL_PERFORMANCE
                    },
                    false,
                    "initializeFromRapidData:legacyModel"
                );
            } else {
                // No model at all - show empty state
                set(
                    { hasLegacyModel: false, currentModelChange: null },
                    false,
                    "initializeFromRapidData:noModel"
                );
            }
            return;
        }

        // Has model changes history
        set({ hasLegacyModel: false }, false, "initializeFromRapidData:hasHistory");

        // Check for unseen changes
        const latestChange = changes[changes.length - 1];
        if (!latestChange.seenBy.includes(userId)) {
            // User hasn't seen the latest change
            // Check if this is a first model (no previous model to compare)
            const isFirstModel = !latestChange.from?.ref;
            set(
                {
                    currentModelChange: latestChange,
                    modelPageState: isFirstModel
                        ? PageState.SINGLE_MODEL_PERFORMANCE
                        : PageState.NEW_MODEL_COMPARISON
                },
                false,
                isFirstModel
                    ? "initializeFromRapidData:firstModel"
                    : "initializeFromRapidData:unseenChange"
            );
        } else {
            // User has seen all changes - show single model performance view
            set(
                {
                    currentModelChange: latestChange,
                    modelPageState: PageState.SINGLE_MODEL_PERFORMANCE
                },
                false,
                "initializeFromRapidData:seenAll"
            );
        }
    },

    markCurrentChangeAsSeen: async (rapidDataId: string, userId: string) => {
        const currentChange = get().currentModelChange;
        if (!currentChange) return;

        if (currentChange.seenBy?.includes(userId)) return;

        // Legacy models have no change ID to mark in the DB
        if (currentChange.id === "legacy") return;

        try {
            await markModelChangeAsSeen(rapidDataId, currentChange.id);

            // Update seenBy locally so widgets outside the modal reflect the change.
            // Don't change modelPageState or clear metrics — the modal keeps showing
            // the same view it was opened with. The next time initializeFromRapidData
            // runs (e.g., on the next modal open), it will read the updated seenBy.
            const updatedChange = { ...currentChange, seenBy: [...currentChange.seenBy, userId] };
            const changes = get().modelChanges.map((c) =>
                c.id === currentChange.id ? updatedChange : c
            );

            set(
                { currentModelChange: updatedChange, modelChanges: changes },
                false,
                "markCurrentChangeAsSeen:success"
            );
        } catch (error) {
            console.error("Failed to mark model change as seen:", error);
        }
    },

    fetchCurrentModelMetrics: async () => {
        const currentChange = get().currentModelChange;
        if (!currentChange?.to?.ref) {
            // No version reference available (e.g., SAM3 model or legacy without ref)
            set(
                { currentModelMetrics: null, metricsLoading: false, metricsError: null },
                false,
                "fetchCurrentModelMetrics:noRef"
            );
            return;
        }

        try {
            set(
                { metricsLoading: true, metricsError: null },
                false,
                "fetchCurrentModelMetrics:start"
            );

            const response = await getVersionMetrics(currentChange.to.ref);

            set(
                { currentModelMetrics: response.metrics, metricsLoading: false },
                false,
                "fetchCurrentModelMetrics:success"
            );
        } catch (error) {
            console.error("Failed to fetch model metrics:", error);
            set(
                {
                    metricsError:
                        error instanceof Error ? error.message : "Failed to fetch metrics",
                    metricsLoading: false
                },
                false,
                "fetchCurrentModelMetrics:error"
            );
        }
    },

    fetchComparisonMetrics: async () => {
        const currentChange = get().currentModelChange;
        const pageState = get().modelPageState;

        if (!currentChange?.to?.ref) {
            // No version reference available
            set(
                {
                    currentModelMetrics: null,
                    previousModelMetrics: null,
                    currentClassMetrics: null,
                    previousClassMetrics: null,
                    metricsLoading: false,
                    metricsError: null
                },
                false,
                "fetchComparisonMetrics:noRef"
            );
            return;
        }

        // Only fetch previous model metrics when in comparison mode
        const shouldFetchPrevious =
            pageState === PageState.NEW_MODEL_COMPARISON && !!currentChange.from?.ref;

        try {
            set(
                { metricsLoading: true, metricsError: null },
                false,
                "fetchComparisonMetrics:start"
            );

            // Fetch current model metrics
            const currentMetricsPromise = getVersionMetrics(currentChange.to.ref);

            // Fetch previous model metrics only in comparison mode
            const previousMetricsPromise = shouldFetchPrevious
                ? getVersionMetrics(currentChange.from!.ref!)
                : Promise.resolve({ metrics: null, classMetrics: null });

            const [currentResponse, previousResponse] = await Promise.all([
                currentMetricsPromise,
                previousMetricsPromise
            ]);

            set(
                {
                    currentModelMetrics: currentResponse.metrics,
                    previousModelMetrics: previousResponse.metrics,
                    currentClassMetrics: currentResponse.classMetrics,
                    previousClassMetrics: previousResponse.classMetrics,
                    metricsLoading: false
                },
                false,
                "fetchComparisonMetrics:success"
            );
        } catch (error) {
            console.error("Failed to fetch comparison metrics:", error);
            set(
                {
                    metricsError:
                        error instanceof Error ? error.message : "Failed to fetch metrics",
                    metricsLoading: false
                },
                false,
                "fetchComparisonMetrics:error"
            );
        }
    },

    fetchComparisonSources: async (rapidDataId: string, apiKey: string) => {
        // Claim a new fetch ID. Any previous in-flight call with an older ID
        // will stop writing to state once it notices it's been superseded.
        const fetchId = ++_activeFetchComparisonId;

        const currentChange = get().currentModelChange;
        if (!currentChange) {
            set(
                { comparisonSources: [], comparisonLoading: false, comparisonError: null },
                false,
                "fetchComparisonSources:noChange"
            );
            return;
        }

        // Collect model refs to compare (current model + previous if available)
        const modelRefs: string[] = [];
        if (currentChange.to?.ref) {
            modelRefs.push(currentChange.to.ref);
        }
        if (currentChange.from?.ref) {
            modelRefs.push(currentChange.from.ref);
        }

        if (modelRefs.length === 0) {
            // No model refs available (e.g., SAM3 models)
            set(
                { comparisonSources: [], comparisonLoading: false, comparisonError: null },
                false,
                "fetchComparisonSources:noRefs"
            );
            return;
        }

        try {
            set(
                { comparisonLoading: true, comparisonError: null },
                false,
                "fetchComparisonSources:start"
            );

            // Step 1: Fetch test sources (simple GET, no inference)
            const response = await getTestSources(rapidDataId, MODEL_COMPARISON_IMAGE_LIMIT);

            // After the async call, check if we've been superseded
            if (fetchId !== _activeFetchComparisonId) {
                return;
            }

            const sources = response.sources;

            if (sources.length === 0) {
                set(
                    { comparisonSources: [], comparisonLoading: false },
                    false,
                    "fetchComparisonSources:noSources"
                );
                return;
            }

            // Step 2: Initialize sources, preserving any existing inference results.
            // When the effect re-fires (e.g., from Firestore listener updates), we don't
            // want to wipe out inference results that already completed successfully.
            const existingSources = get().comparisonSources;
            const existingInferencesMap = new Map(existingSources.map((s) => [s.id, s.inferences]));

            const sourcesWithInference: TestSourceWithInference[] = sources.map((source) => ({
                ...source,
                inferences: existingInferencesMap.get(source.id) || {}
            }));

            // Set sources and mark loading as done - images will show immediately
            // Individual inference loading is tracked per-source via empty inferences object
            set(
                { comparisonSources: sourcesWithInference, comparisonLoading: false },
                false,
                "fetchComparisonSources:sourcesLoaded"
            );

            // Step 3: Run inference for each source and each model ref
            // Run inference in parallel for all sources, skipping any that already have results
            await Promise.all(
                sources.map(async (source) => {
                    // Run inference for each model ref on this source
                    await Promise.all(
                        modelRefs.map(async (modelRef) => {
                            // Skip if we already have a successful result for this source/model
                            const existingInference = existingInferencesMap.get(source.id);
                            if (existingInference?.[modelRef]?.result) {
                                return;
                            }

                            try {
                                let inferenceResult: any = null;

                                // Use streamInfer with MODEL inference type
                                await streamInfer(
                                    {
                                        fileId: source.id,
                                        fps: 1, // Not used for images, but required by schema
                                        inferenceType: InferenceTypes.MODEL,
                                        apiKey,
                                        modelId: modelRef,
                                        imageUrl: source.imageUrl,
                                        confidence: 0.4,
                                        overlap: 0.3
                                    },
                                    {
                                        onResult: (data) => {
                                            inferenceResult = data.result;
                                        },
                                        onError: (data) => {
                                            console.error(
                                                `Inference error for ${source.id} with ${modelRef}:`,
                                                data.message
                                            );
                                        }
                                    }
                                );

                                // Guard: if a newer fetchComparisonSources call has started,
                                // this call is stale — don't overwrite state.
                                if (fetchId !== _activeFetchComparisonId) {
                                    return;
                                }

                                // Guard: if handlers were never called (e.g., streamInfer dedup
                                // returned existing promise without wiring up our handlers),
                                // don't overwrite an existing good result with null.
                                if (inferenceResult === null) {
                                    const existing = get().comparisonSources.find(
                                        (s) => s.id === source.id
                                    );
                                    if (existing?.inferences?.[modelRef]?.result) {
                                        return;
                                    }
                                }

                                // Update state with inference result
                                const currentSources = get().comparisonSources;
                                const updatedSources = currentSources.map((s) => {
                                    if (s.id === source.id) {
                                        return {
                                            ...s,
                                            inferences: {
                                                ...s.inferences,
                                                [modelRef]: { modelRef, result: inferenceResult }
                                            }
                                        };
                                    }
                                    return s;
                                });

                                set(
                                    { comparisonSources: updatedSources },
                                    false,
                                    `fetchComparisonSources:inferenceComplete:${source.id}:${modelRef}`
                                );
                            } catch (error) {
                                console.error(
                                    `Failed to run inference for ${source.id} with ${modelRef}:`,
                                    error
                                );

                                // Guard: don't write state if a newer call has superseded this one
                                if (fetchId !== _activeFetchComparisonId) return;

                                // Update state with error for this source/model combination
                                const currentSources = get().comparisonSources;
                                const updatedSources = currentSources.map((s) => {
                                    if (s.id === source.id) {
                                        return {
                                            ...s,
                                            inferences: {
                                                ...s.inferences,
                                                [modelRef]: {
                                                    modelRef,
                                                    result: null,
                                                    error:
                                                        error instanceof Error
                                                            ? error.message
                                                            : "Inference failed"
                                                }
                                            }
                                        };
                                    }
                                    return s;
                                });

                                set(
                                    { comparisonSources: updatedSources },
                                    false,
                                    `fetchComparisonSources:inferenceError:${source.id}:${modelRef}`
                                );
                            }
                        })
                    );
                })
            );
            // Note: comparisonLoading was already set to false after sources loaded
            // Individual inference results update the state progressively
        } catch (error) {
            console.error("Failed to fetch comparison sources:", error);
            set(
                {
                    comparisonError:
                        error instanceof Error
                            ? error.message
                            : "Failed to fetch comparison sources",
                    comparisonLoading: false
                },
                false,
                "fetchComparisonSources:error"
            );
        }
    },

    addUploadedSource: (source: TestSourceWithInference) => {
        const currentSources = get().comparisonSources;
        set(
            { comparisonSources: [...currentSources, source] },
            false,
            `addUploadedSource:${source.id}`
        );
    },

    /**
     * Run inference on an uploaded image via the standard api/infer SSE endpoint.
     *
     * The file should already be uploaded to Firebase Storage (via MediaProvider).
     * This action calls streamInfer with the fileId — the backend resolves it
     * to a signed GCS URL via storageService.getReadablePublicUrl(fileId).
     * Runs inference for each model ref and updates comparisonSources progressively.
     */
    runInferenceForSource: async (source: { id: string; fileId: string }, apiKey: string) => {
        const currentChange = get().currentModelChange;
        if (!currentChange) return;

        const modelRefs: string[] = [];
        if (currentChange.to?.ref) modelRefs.push(currentChange.to.ref);
        if (currentChange.from?.ref) modelRefs.push(currentChange.from.ref);

        if (modelRefs.length === 0) return;

        const { fileId } = source;

        await Promise.all(
            modelRefs.map(async (modelRef) => {
                try {
                    let inferenceResult: any = null;

                    await streamInfer(
                        {
                            fileId,
                            fps: 1,
                            inferenceType: InferenceTypes.MODEL,
                            apiKey,
                            modelId: modelRef,
                            confidence: 0.4,
                            overlap: 0.3
                        },
                        {
                            onResult: (data) => {
                                inferenceResult = data.result;
                            },
                            onError: (data) => {
                                console.error(
                                    `Inference error for uploaded ${source.id} with ${modelRef}:`,
                                    data.message
                                );
                            }
                        }
                    );

                    const currentSources = get().comparisonSources;
                    const updatedSources = currentSources.map((s) => {
                        if (s.id === source.id) {
                            return {
                                ...s,
                                inferences: {
                                    ...s.inferences,
                                    [modelRef]: { modelRef, result: inferenceResult }
                                }
                            };
                        }
                        return s;
                    });

                    set(
                        { comparisonSources: updatedSources },
                        false,
                        `runInferenceForSource:complete:${source.id}:${modelRef}`
                    );
                } catch (error) {
                    console.error(
                        `Failed inference for uploaded ${source.id} with ${modelRef}:`,
                        error
                    );
                    const currentSources = get().comparisonSources;
                    const updatedSources = currentSources.map((s) => {
                        if (s.id === source.id) {
                            return {
                                ...s,
                                inferences: {
                                    ...s.inferences,
                                    [modelRef]: {
                                        modelRef,
                                        result: null,
                                        error:
                                            error instanceof Error
                                                ? error.message
                                                : "Inference failed"
                                    }
                                }
                            };
                        }
                        return s;
                    });

                    set(
                        { comparisonSources: updatedSources },
                        false,
                        `runInferenceForSource:error:${source.id}:${modelRef}`
                    );
                }
            })
        );
    },

    clearModelState: () =>
        set(
            {
                modelPageState: PageState.LOADING,
                currentModelChange: null,
                modelChanges: [],
                hasLegacyModel: false,
                modelChangeLoading: false,
                modelChangeError: null,
                currentModelMetrics: null,
                previousModelMetrics: null,
                currentClassMetrics: null,
                previousClassMetrics: null,
                metricsLoading: false,
                metricsError: null,
                comparisonSources: [],
                comparisonLoading: false,
                comparisonError: null
            },
            false,
            "clearModelState"
        )
});
